import * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AdminNotificationFlowIssueList } from '@/components/admin/admin-notification-flow-issue-list';
import { SeverityIcon } from '@/components/admin/admin-notification-stack-shared';
import {
  isAdminNotificationSopFlowType,
  resolveAdminNotificationSopId,
} from '@/lib/admin-notification-flow-issues';
import { relativeAgeFromIso } from '@/lib/admin-notification-stack.types';
import { categoryLabel, type AdminNotification } from '@/lib/notifications';
import { isCanonicalSopStructuralNotificationType } from '@/lib/admin-notification-entity-scope';

function notificationSubtitle(row: AdminNotification): string | undefined {
  const context = row.context?.trim() || '';
  const details = row.details?.trim() || '';
  if (context && context !== row.message.trim()) {
    if (details && details !== context && details !== row.message.trim()) {
      return `${context}\n${details}`;
    }
    return context;
  }
  if (details && details !== row.message.trim()) return details;
  return undefined;
}

export function AdminNotificationEntityAlertsDialog({
  alerts,
  open,
  suppressedScenarioCount = 0,
  onOpenChange,
}: {
  alerts: AdminNotification[];
  open: boolean;
  suppressedScenarioCount?: number;
  onOpenChange: (open: boolean) => void;
}) {
  const count = alerts.length;
  const title =
    count > 1 ? `${count} critical issues need attention` : 'Critical issue needs attention';

  return (
    <Dialog open={open} onOpenChange={onOpenChange} analyticsFeatureId="surface.components.admin.admin-notification-entity-alerts-dialog.dialog-1">
      <DialogContent elevated className="max-h-[85vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SeverityIcon severity="critical" className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Review these errors before fixing. Snoozing in Action Center does not resolve the underlying problems.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {alerts.map((row) => {
            const subtitle = notificationSubtitle(row);
            const sopId = resolveAdminNotificationSopId(row);
            const showFlowIssues = sopId != null && isAdminNotificationSopFlowType(row.type);
            const showSuppressedScenarios =
              suppressedScenarioCount > 0 && isCanonicalSopStructuralNotificationType(row.type);
            return (
              <div
                key={row.key}
                className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="font-medium">
                    {categoryLabel(row.category)}
                  </Badge>
                  <Badge variant="destructive">Critical</Badge>
                  <span className="text-xs text-muted-foreground">
                    {relativeAgeFromIso(new Date(row.timestamp).toISOString())}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{row.message}</p>
                {subtitle ? (
                  <p className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">{subtitle}</p>
                ) : null}
                {showSuppressedScenarios ? (
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Also blocks {suppressedScenarioCount} enabled scenario
                    {suppressedScenarioCount === 1 ? '' : 's'} across sites. Fix the SOP workflow below.
                  </p>
                ) : null}
                {showFlowIssues ? <AdminNotificationFlowIssueList sopId={sopId} /> : null}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Continue to fix
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
